import {
	AdditiveBlending,
	CanvasTexture,
	DoubleSide,
	Mesh,
	MeshBasicMaterial,
	Object3D,
	PlaneGeometry,
	PointLight,
	PositionalAudio,
	Sprite,
	SpriteMaterial,
	Texture,
	Vector3,
} from 'three';
import { DEFAULT_FX } from '../hybrid/fx-palette.js';

// white radial glow — tintable via material.color (a colored texture would
// multiply against the tint and darken it). Shared by laser impacts and
// engine glows.
let impactTexture = null;
export function getRadialGlowTexture() {
	if (impactTexture) return impactTexture;
	const canvas = document.createElement('canvas');
	canvas.width = 64;
	canvas.height = 64;
	const context = canvas.getContext('2d');
	const gradient = context.createRadialGradient(32, 32, 0, 32, 32, 32);
	gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
	gradient.addColorStop(0.3, 'rgba(255, 255, 255, 0.6)');
	gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
	context.fillStyle = gradient;
	context.fillRect(0, 0, 64, 64);
	impactTexture = new CanvasTexture(canvas);
	return impactTexture;
}

const _beamOrigin = new Vector3();

export default class LaserBeam {
	duration = 250;
	active = false;

	constructor(parent, { color = DEFAULT_FX.weapon } = {}) {
		this.parent = parent;
		this.target = new Vector3();
		this.object3d = new Object3D();
		this.object3d.name = 'LASER_BEAM';

		const canvas = document.createElement('canvas');
		canvas.width = 1;
		canvas.height = 64;
		const context = canvas.getContext('2d');
		// neutral gradient — the hue comes from material.color so weapons
		// are tintable per race/tech
		const gradient = context.createLinearGradient(0, 0, canvas.width, canvas.height);
		gradient.addColorStop(0, 'rgba(0, 0, 0, 0.1)');
		gradient.addColorStop(0.1, 'rgba(200, 200, 200, 0.5)');
		gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.95)');
		gradient.addColorStop(0.9, 'rgba(200, 200, 200, 0.5)');
		gradient.addColorStop(1, 'rgba(0, 0, 0, 0.1)');
		context.fillStyle = gradient;
		context.fillRect(0, 0, canvas.width, canvas.height);

		const texture = new Texture(canvas);
		texture.needsUpdate = true;
		this.material = new MeshBasicMaterial({
			map: texture,
			blending: AdditiveBlending,
			color,
			side: DoubleSide,
			depthWrite: false,
			transparent: true,
		});
		const geometry = new PlaneGeometry(1, 0.1);
		geometry.rotateY(Math.PI / 2);
		for (let i = 0; i < 16; i++) {
			const mesh = new Mesh(geometry, this.material);
			mesh.position.z = 0.5;
			mesh.rotation.z = (i / 16) * Math.PI;
			mesh.renderOrder = 810; // above shield bubbles (800), below unit markers (900+)
			this.object3d.add(mesh);
		}
		this.object3d.scale.set(0.33, 0.33, 0);

		const impact = new Sprite(new SpriteMaterial({
			map: getRadialGlowTexture(),
			color,
			blending: AdditiveBlending,
			transparent: true,
		}));
		impact.scale.set(4, 4, 4);
		impact.position.z = 0.99;
		impact.renderOrder = 811;
		impact.visible = false;
		// idle beams are NOT drawn: scale 0 still cost 16 two-pass transparent draws per
		// unit per frame (160 planes in view on the Pico, 2026-10-10)
		this.object3d.visible = false;
		this.impact = impact;
		this.object3d.add(impact);

		this.light = new PointLight(color, 0.5, 120);
		impact.add(this.light);

		game.scene.add(this.object3d);
	}

	setColor(color) {
		this.material.color.setHex(color);
		this.impact.material.color.setHex(color);
		this.light.color.setHex(color);
	}

	// getEndPoint (optional) re-resolves the endpoint every frame — so the beam
	// stops on the target's shield bubble while it's up, reaches the hull once
	// it collapsed mid-beam, and follows moving targets
	fireAt(position, getEndPoint = null) {
		this.start = Date.now();
		this.getEndPoint = getEndPoint;
		this.impactShownAt = null;
		this.target.set(position.x, position.y, position.z);
		this.active = true;
		this.object3d.visible = true;
		this.object3d.position.copy(this.getOrigin());

		if (game.audioListenerPhaser && game.audioBufferPhaser) {
			const sound = new PositionalAudio(game.audioListenerPhaser);
			sound.setBuffer(game.audioBufferPhaser);
			sound.setRefDistance(60);
			this.object3d.add(sound);
			this.sound = sound;
			setTimeout(() => sound.play(), 100);
		}
		return this;
	}

	// the beam lives in the scene, but an orbiting unit's mesh is a child of
	// its planet group — copying mesh.position raw would read planet-local
	// coords as scene coords and fire the beam from a phantom position
	getOrigin() {
		this.parent.getWorldPosition(_beamOrigin);
		game.main.worldToLocal(_beamOrigin);
		return _beamOrigin;
	}

	render() {
		const now = Date.now();
		const elapsed = now - this.start;
		if (this.getEndPoint) {
			const p = this.getEndPoint();
			if (p) this.target.set(p.x, p.y, p.z);
		}
		this.object3d.position.copy(this.getOrigin());
		this.object3d.lookAt(this.target);
		const distance = this.object3d.position.distanceTo(this.target);
		if (elapsed < this.duration * 0.95) {
			this.object3d.scale.z = (distance / this.duration) * elapsed;
			return;
		}
		// arrival: pin the beam at full length and show the impact flash for a
		// guaranteed minimum time — the old window (95%..125% of duration) was
		// ~75ms and a single dropped frame skipped the flash entirely
		if (!this.impactShownAt) {
			this.impactShownAt = now;
			this.impact.visible = true;
		}
		this.object3d.scale.z = distance;
		if (now > this.impactShownAt + 120 && elapsed > this.duration * 1.25) {
			this.object3d.scale.z = 0;
			this.object3d.visible = false;
			this.active = false;
			this.impact.visible = false;
			this.impactShownAt = null;
			this.getEndPoint = null;
			if (this.sound) {
				this.object3d.remove(this.sound);
				this.sound = null;
			}
		}
	}

	dispose() {
		game.disposeHierarchy?.(this.object3d);
	}
}
